import React, { useEffect, useRef } from 'react';
import { formatDate, formatDateTime } from '../lib/datetime';
import Icon, { ICON_NAMES } from './Icon';

// Shared presentational building blocks + style tokens for admin pages.
// Keeps every admin page visually consistent and handles the empty / loading /
// error states so a null/empty API response never turns into a broken screen.

export function PageHeader({ title, subtitle, children }) {
  return (
    <div style={ui.pageHeader}>
      <div>
        <h1 style={ui.title}>{title}</h1>
        {subtitle && <p style={ui.subtitle}>{subtitle}</p>}
      </div>
      {children && <div style={ui.headerActions}>{children}</div>}
    </div>
  );
}

export function Loading({ text = 'Yuklanmoqda...' }) {
  return <div style={ui.centerMsg}>{text}</div>;
}

export function ErrorBanner({ children }) {
  if (!children) return null;
  return <div style={ui.errorBanner}>{children}</div>;
}

// `icon` — Icon.js dagi chiziqli ikonka nomi (noma'lum nom bo'lsa 'inbox').
// Ixtiyoriy `actionLabel` + `onAction` — sariq primary tugma.
export function Empty({ icon = 'inbox', title = "Hali ma'lumot yo'q", hint, actionLabel, onAction }) {
  return (
    <div style={ui.empty}>
      <div style={ui.emptyIcon}>
        <Icon name={ICON_NAMES.includes(icon) ? icon : 'inbox'} size={48} color="var(--color-primary)" strokeWidth={1.75} />
      </div>
      <p style={ui.emptyText}>{title}</p>
      {hint && <p style={ui.emptyHint}>{hint}</p>}
      {actionLabel && onAction && (
        <button type="button" style={{ ...ui.addBtn, marginTop: '16px' }} onClick={onAction}>
          {actionLabel}
        </button>
      )}
    </div>
  );
}

export function Badge({ children, tone = 'blue' }) {
  return <span style={{ ...ui.badge, ...(TONES[tone] || TONES.blue) }}>{children}</span>;
}

// Modal: Escape, fonni bosish va burchakdagi × har doim yopadi (yuklanish / xato / muvaffaqiyat
// holatlarida ham). `onClose` har doim uzatilishi kerak. `busy` — so'rov davomida yopilmasin.
export function Modal({ title, onClose, children, maxWidth = '520px', busy = false }) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (busy) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && closeRef.current) closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy]);
  const close = () => { if (!busy && onClose) onClose(); };
  return (
    <div style={ui.overlay} onClick={close}>
      <div
        style={{ ...ui.modalCard, maxWidth, position: 'relative' }}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" style={ui.modalX} onClick={close} aria-label="Yopish" title="Yopish">
          <Icon name="x" size={18} />
        </button>
        {title && <h2 style={{ ...ui.modalTitle, paddingRight: '36px' }}>{title}</h2>}
        {children}
      </div>
    </div>
  );
}

// Tasdiq dialogi (Modal ustida): Bekor qilish / Tasdiqlash. `busy` — tugmalar disabled.
export function ConfirmDialog({
  title = 'Tasdiqlang', message, confirmLabel = 'Tasdiqlash', cancelLabel = 'Bekor qilish',
  danger = false, busy = false, busyLabel = 'Bajarilmoqda...', onConfirm, onCancel,
}) {
  return (
    <Modal title={title} onClose={onCancel} maxWidth="420px" busy={busy}>
      {message && <p style={ui.confirmText}>{message}</p>}
      <div style={ui.modalActions}>
        <button type="button" style={{ ...ui.cancelBtn, ...(busy ? ui.btnDisabled : {}) }} onClick={onCancel} disabled={busy}>
          {cancelLabel}
        </button>
        <button
          type="button"
          style={{ ...ui.saveBtn, ...(danger ? { background: 'var(--color-danger)', color: '#fff' } : {}), ...(busy ? ui.btnDisabled : {}) }}
          onClick={onConfirm}
          disabled={busy}
        >
          {busy ? busyLabel : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

// Qalqib chiquvchi xabar: { type: 'success' | 'error', text }. 4 soniyadan keyin onClose.
export function Toast({ toast, onClose }) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => closeRef.current && closeRef.current(), 4000);
    return () => clearTimeout(t);
  }, [toast]);
  if (!toast) return null;
  const ok = toast.type !== 'error';
  return (
    <div
      className="th-pop"
      role={ok ? 'status' : 'alert'}
      style={{ ...ui.toast, background: ok ? 'var(--color-success)' : 'var(--color-danger)' }}
    >
      <span>{toast.text}</span>
      <button type="button" style={ui.toastX} onClick={onClose} aria-label="Yopish">
        <Icon name="x" size={14} />
      </button>
    </div>
  );
}

// Formats a backend ISO timestamp for display. Timezone-safe (no ±5h drift)
// and never shows seconds — delegates to the shared datetime helper.
export function fmtDate(iso, withTime = false) {
  const r = withTime ? formatDateTime(iso) : formatDate(iso);
  return r || '—';
}

// Extracts an array payload from the various shapes the backend uses
// ({items:[]}, {data:[]}, or a bare array). Never throws on empty/null.
export function asList(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.items)) return data.items;
  if (data && Array.isArray(data.data)) return data.data;
  return [];
}

// Status yorliqlari faqat 5 ta variantda: dark / primary / neutral / success / danger.
// Eski ohang nomlari (blue/green/red/amber/gray/purple) shu variantlarga xaritalanadi.
const VARIANT = {
  dark: { background: 'var(--color-dark)', color: 'var(--color-on-dark)' },
  primary: { background: 'var(--color-primary)', color: 'var(--color-on-primary)' },
  neutral: { background: 'var(--color-surface-alt)', color: 'var(--color-text)' },
  // success / danger: ~12% shaffof fon + to'liq rangli matn
  success: { background: 'rgba(46, 158, 91, 0.12)', color: 'var(--color-success)' },
  danger: { background: 'rgba(229, 72, 77, 0.12)', color: 'var(--color-danger)' },
};
const TONES = {
  ...VARIANT,
  blue: VARIANT.neutral,
  green: VARIANT.success,
  red: VARIANT.danger,
  amber: VARIANT.primary,
  gray: VARIANT.neutral,
  purple: VARIANT.dark,
};

const FIELD_BASE = {
  width: '100%',
  padding: '10px 14px',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-input)',
  fontSize: '14px',
  fontWeight: '400',
  color: 'var(--color-text)',
  outline: 'none',
  boxSizing: 'border-box',
  backgroundColor: 'var(--color-surface)',
  fontFamily: 'var(--font-family)',
};

// Select uchun SVG chevron (appearance: none bilan birga). Rang #777777.
const CHEVRON =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23777777' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='m6 9 6 6 6-6'/></svg>\")";

const BTN_BASE = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 'var(--radius-btn)',
  fontFamily: 'var(--font-family)',
  fontSize: '14px',
  cursor: 'pointer',
  border: '1px solid transparent',
};

export const ui = {
  pageHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: '24px',
    gap: '16px',
    flexWrap: 'wrap',
  },
  sectionTitle: { margin: '0 0 12px', fontSize: '18px', fontWeight: '700', color: 'var(--color-text)' },
  title: { margin: '0 0 4px', fontSize: '28px', fontWeight: '700', color: 'var(--color-text)' },
  subtitle: { margin: 0, fontSize: '14px', color: 'var(--color-text-muted)' },
  headerActions: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  // primary: sariq fon, qora Bold matn
  addBtn: {
    ...BTN_BASE,
    minHeight: '44px',
    padding: '10px 20px',
    background: 'var(--color-primary)',
    color: 'var(--color-on-primary)',
    fontWeight: '700',
    whiteSpace: 'nowrap',
  },
  errorBanner: {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-danger)',
    borderRadius: 'var(--radius-input)',
    padding: '12px 16px',
    color: 'var(--color-danger)',
    fontSize: '14px',
    marginBottom: '16px',
  },
  // ogohlantirish / kutilmoqda: sariq fon + qora matn
  infoBanner: {
    background: 'var(--color-primary)',
    borderRadius: 'var(--radius-input)',
    padding: '12px 16px',
    color: 'var(--color-on-primary)',
    fontSize: '14px',
    marginBottom: '16px',
    lineHeight: 1.5,
  },
  centerMsg: { textAlign: 'center', padding: '60px', color: 'var(--color-text-muted)', fontSize: '14px' },
  // markazlashgan blok: yuqoriga yopishmaydi, karta/chegarasiz
  empty: {
    minHeight: '320px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '48px 24px',
    background: 'transparent',
  },
  emptyIcon: {
    margin: '0 auto 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: { margin: '0 0 6px', fontSize: '18px', fontWeight: '700', color: 'var(--color-text)' },
  emptyHint: { margin: 0, fontSize: '14px', color: 'var(--color-text-muted)' },
  tableWrap: {
    background: 'var(--color-surface)',
    borderRadius: 'var(--radius-card)',
    border: '1px solid var(--color-border)',
    overflowY: 'hidden',
    overflowX: 'auto',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: '500',
    color: 'var(--color-text-muted)',
    background: 'var(--color-surface-alt)',
    borderBottom: '1px solid var(--color-border)',
    fontSize: '12px',
    whiteSpace: 'nowrap',
  },
  tr: { borderBottom: '1px solid var(--color-border)' },
  td: { padding: '12px 16px', verticalAlign: 'middle', fontSize: '14px', color: 'var(--color-text)' },
  badge: {
    display: 'inline-block',
    whiteSpace: 'nowrap',
    padding: '4px 10px',
    borderRadius: 'var(--radius-chip)',
    fontSize: '12px',
    fontWeight: '500',
  },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' },
  btnSm: {
    ...BTN_BASE,
    minHeight: '36px',
    padding: '6px 14px',
    fontSize: '14px',
    fontWeight: '700',
  },
  // outline (neytral amal)
  btnBlue: { background: 'var(--color-surface)', color: 'var(--color-text)', border: '1px solid var(--color-border)' },
  // asosiy / tasdiqlash amali → primary
  btnGreen: { background: 'var(--color-primary)', color: 'var(--color-on-primary)', border: '1px solid var(--color-primary)', fontWeight: '700' },
  // o'chirish / rad etish / bloklash → danger
  // ikkilamchi danger: oq fon, 1px danger chegara, danger matn (to'ldirilgan qizil fon yo'q)
  btnRed: { background: 'var(--color-surface)', color: 'var(--color-danger)', border: '1px solid var(--color-danger)' },
  // fon va chegarasiz danger link-tugma (qatordagi Bloklash / Blokdan chiqarish)
  btnLinkDanger: {
    background: 'transparent',
    color: 'var(--color-danger)',
    border: '1px solid transparent',
    fontWeight: '500',
    padding: '6px 8px',
  },
  btnGray: { background: 'var(--color-surface-alt)', color: 'var(--color-text)', border: '1px solid var(--color-surface-alt)' },
  searchInput: {
    ...FIELD_BASE,
    width: 'auto',
    height: '48px',
    minWidth: '240px',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(17,17,17,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 200,
    padding: '16px',
  },
  modalCard: {
    background: 'var(--color-surface)',
    borderRadius: 'var(--radius-card)',
    padding: '24px',
    width: '100%',
    boxShadow: 'var(--shadow-pop)',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  modalX: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    width: '32px',
    height: '32px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    color: 'var(--color-text-muted)',
    border: 'none',
    borderRadius: 'var(--radius-btn)',
    cursor: 'pointer',
    padding: 0,
  },
  confirmText: { margin: '0 0 16px', fontSize: '14px', lineHeight: 1.5, color: 'var(--color-text)' },
  toast: {
    position: 'fixed',
    top: '70px',
    right: '16px',
    left: 'auto',
    maxWidth: 'calc(100vw - 32px)',
    zIndex: 400,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderRadius: 'var(--radius-input)',
    color: '#FFFFFF',
    fontSize: '14px',
    fontWeight: '500',
  },
  toastX: {
    display: 'inline-flex',
    background: 'transparent',
    color: '#FFFFFF',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
  },
  modalTitle: { margin: '0 0 16px', fontSize: '18px', fontWeight: '700', color: 'var(--color-text)' },
  field: { marginBottom: '16px' },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--color-text)',
  },
  req: { color: 'var(--color-danger)' },
  input: { ...FIELD_BASE, height: '48px' },
  textarea: { ...FIELD_BASE, resize: 'vertical', minHeight: '96px', lineHeight: 1.45 },
  select: {
    ...FIELD_BASE,
    height: '48px',
    cursor: 'pointer',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    paddingRight: '40px',
    backgroundImage: CHEVRON,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 14px center',
    backgroundSize: '16px 16px',
  },
  // sarlavha qatoridagi filtr select'i: to'liq kenglikka cho'zilmaydi
  headerSelect: { width: 'auto', minWidth: '200px' },
  formError: {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-danger)',
    borderRadius: 'var(--radius-input)',
    padding: '10px 12px',
    color: 'var(--color-danger)',
    fontSize: '13px',
    marginBottom: '12px',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    marginTop: '8px',
    flexWrap: 'wrap',
  },
  // outline
  cancelBtn: {
    ...BTN_BASE,
    minHeight: '44px',
    padding: '9px 18px',
    background: 'var(--color-surface)',
    color: 'var(--color-text)',
    border: '1px solid var(--color-border)',
    fontWeight: '500',
  },
  // primary
  saveBtn: {
    ...BTN_BASE,
    minHeight: '44px',
    padding: '9px 22px',
    background: 'var(--color-primary)',
    color: 'var(--color-on-primary)',
    fontWeight: '700',
  },
  btnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  // pagination
  pager: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    marginTop: '16px',
    flexWrap: 'wrap',
  },
  pagerBtn: {
    ...BTN_BASE,
    minHeight: '44px',
    padding: '8px 16px',
    whiteSpace: 'nowrap',
    background: 'var(--color-surface)',
    color: 'var(--color-text)',
    border: '1px solid var(--color-border)',
    fontWeight: '700',
  },
  pagerInfo: { fontSize: '14px', color: 'var(--color-text-muted)' },
  // stat cards
  statGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: '8px',
    marginBottom: '24px',
  },
  // to'liq kenglikdagi 3 ustunli to'r (kichik ekranda .th-stat-grid-3 moslashadi)
  statGrid3: { gap: '8px', marginBottom: '24px' },
  // teng to'r: kenglikka qarab ustunlar soni o'zgaradi
  statGridFit: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '8px',
    marginBottom: '24px',
  },
  statCard: {
    background: 'var(--color-surface-alt)',
    borderRadius: 'var(--radius-card)',
    padding: '16px',
  },
  // urg'uli karta: qora fon + oq yorliq + sariq raqam
  statCardAccent: {
    background: 'var(--color-dark)',
    borderRadius: 'var(--radius-card)',
    padding: '16px',
  },
  statValue: { fontSize: '28px', fontWeight: '700', color: 'var(--color-text)', lineHeight: 1.1 },
  statLabel: {
    fontSize: '12px',
    color: 'var(--color-text-muted)',
    marginTop: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  statDot: { width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0 },
};

// `dot` — yorliq yonidagi kichik urg'u nuqtasi rangi (ixtiyoriy).
// `highlight` — urg'uli (qora) karta.
// `onClick` — karta bosiladigan bo'ladi (filtr sifatida); `selected` — tanlangan karta (sariq chegara).
export function StatCard({ label, value, accent = 'var(--color-text)', dot, highlight = false, onClick, selected = false }) {
  const body = (
    <>
      <div style={{ ...ui.statValue, color: highlight ? 'var(--color-primary)' : accent }}>{value}</div>
      <div style={{ ...ui.statLabel, ...(highlight ? { color: 'var(--color-on-dark)' } : {}) }}>
        {dot && <span style={{ ...ui.statDot, background: dot }} />}
        {label}
      </div>
    </>
  );
  const base = highlight ? ui.statCardAccent : ui.statCard;
  if (!onClick) return <div style={base}>{body}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      style={{
        ...base,
        border: `1px solid ${selected ? 'var(--color-primary)' : 'transparent'}`,
        textAlign: 'left',
        cursor: 'pointer',
        font: 'inherit',
        width: '100%',
      }}
    >
      {body}
    </button>
  );
}
